import React from "react";
import {
    Box,
    Typography,
    Chip,
    IconButton,
    TablePagination,
    Button,
    CircularProgress,
    TextField,
    InputAdornment,
    Menu,
    MenuItem,
    Divider,
    Tooltip,
    Select,
    FormControl,
} from "@mui/material";
import InvoiceMobileCard from './InvoiceMobileCard';
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import DynamicSearchBar from "../../../pages/purchaseInvoices/SearchBar";
import { fieldsToSearch } from '../../../constants/invoiceTableConstants';
import DeleteIcon from '@mui/icons-material/Delete';
import { Pencil } from 'lucide-react';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import IosShareIcon from "@mui/icons-material/IosShare";
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SendIcon from '@mui/icons-material/Send';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { isRequiresAttentionStatus, isApprovedStatus } from '../../../Utils/purchaseInvoices/statusTranslations';
import { APPROVAL_PACKAGE_STATUS } from '../../../constants/purchaseInvoices/approvalPackageConstants';
import { StyledNoResultsOverlay } from "../../../styles/purchaseInvoices/pages/commonTableStyles";
import styles from '../../../styles/purchaseInvoices/pages/InvoiceTableStyles';

function InvoiceMobileView({
    hookData,
    actionMenuAnchorEl,
    activeInvoiceId,
    onActionsClick,
    onActionsClose,
    onOpenDrawer,
    onOpenManualDrawer,
    canEdit,
    canDelete,
    canUpload,
}) {
    const {
        openSearch,
        setOpenSearch,
        activeSearches,
        setActiveSearches,
        globalSearchTerm,
        page,
        rowsPerPage,
        highlightedInvoiceRef,
        setOpenDelete,
        fileAsBase64,
        isLoadingFile,
        errorFile,
        setSelectedFileName,
        statuses,
        selectedStatus,
        setSelectedStatus,
        sortedData,
        paginatedData,
        hasGlobalSearch,
        hasNoSearchResults,
        onRowClick,
        checkingBooknum,
        handleCheckGoodsReceipt,
        handleRetryApprovedInvoice,
        handleRetryRequiresAttentionInvoice,
        handleChangeInvoiceStatus,
        handleGlobalSearch,
        handleSearch,
        handleEditClick,
        handleExportToExcel,
        isAdmin,
        setPage,
        setRowsPerPage,
        canDeleteInvoice,
        isOverdueAttention
    } = hookData;

    return (
        <>
            <Box sx={styles.headerBox}>
                <Box sx={styles.controlsBox}>
                    <FormControl sx={{ width: '100%' }}>
                        <Select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            size="small"
                            dir="rtl"
                            fullWidth
                            sx={{
                                borderRadius: '8px',
                                fontSize: '0.95rem',
                                fontWeight: 500,
                                mb: 1,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#d1d5db'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#1976d2'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#1976d2',
                                    borderWidth: '2px'
                                },
                                '& .MuiSelect-select': {
                                    py: 1.25
                                }
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        maxHeight: 300,
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        '& .MuiMenuItem-root': {
                                            fontSize: '0.95rem',
                                            fontWeight: 500,
                                            py: 1.25,
                                            '&:hover': {
                                                backgroundColor: '#f3f4f6'
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#e3f2fd',
                                                color: '#1976d2',
                                                fontWeight: 600,
                                                '&:hover': {
                                                    backgroundColor: '#bbdefb'
                                                }
                                            }
                                        }
                                    }
                                }
                            }}
                        >
                            {statuses.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {status === "all" ? "כל הסטטוסים" : status}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        placeholder="חיפוש..."
                        value={globalSearchTerm}
                        onChange={(e) => handleGlobalSearch(e.target.value)}
                        size="small"
                        sx={styles.globalSearchInput(false)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: globalSearchTerm && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleGlobalSearch('')}
                                        sx={styles.globalSearchClearButton}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <Button
                        onClick={() => setOpenSearch(true)}
                        variant="outlined"
                        startIcon={<SearchIcon />}
                        sx={styles.advancedSearchButton(false)}
                    >
                        חיפוש מתקדם
                    </Button>

                    <Box sx={styles.chipsBox}>
                        {hasGlobalSearch && (
                            <Chip
                                label={`חיפוש: ${globalSearchTerm}`}
                                onDelete={() => handleGlobalSearch('')}
                                size="small"
                                sx={styles.globalSearchChip}
                            />
                        )}
                        {Object.entries(activeSearches).map(([key, value]) => {
                            const fieldLabel = fieldsToSearch.find(f => f.name === key)?.name || key;
                            return (
                                <Chip
                                    key={key}
                                    label={`${fieldLabel}: ${value}`}
                                    onDelete={() => {
                                        const newSearches = { ...activeSearches };
                                        delete newSearches[key];
                                        setActiveSearches(newSearches);
                                        handleSearch(newSearches, true);
                                    }}
                                    size="small"
                                    sx={styles.advancedSearchChip}
                                />
                            );
                        })}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                    <Button
                        variant="contained"
                        onClick={handleExportToExcel}
                        startIcon={<IosShareIcon />}
                        sx={{
                            backgroundColor: '#2e7d32',
                            color: 'white',
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
                            '&:hover': {
                                backgroundColor: '#1b5e20',
                                boxShadow: '0 4px 12px rgba(46, 125, 50, 0.4)'
                            }
                        }}
                    >
                        ייצוא לאקסל
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={onOpenManualDrawer}
                        color="primary"
                        size="medium"
                        startIcon={<EditIcon />}
                        sx={{
                            borderColor: '#1976d2',
                            color: '#1976d2',
                            py: 1.5,
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            borderWidth: 2,
                            '&:hover': {
                                borderColor: '#1565c0',
                                backgroundColor: '#e3f2fd',
                                borderWidth: 2
                            }
                        }}
                        disabled={!canUpload()}
                    >
                        הזנת חשבונית ידנית
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={onOpenDrawer}
                        color="primary"
                        size="medium"
                        startIcon={<CloudUploadIcon />}
                        sx={{
                            borderColor: '#1976d2',
                            color: '#1976d2',
                            py: 1.5,
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            borderWidth: 2,
                            '&:hover': {
                                borderColor: '#1565c0',
                                backgroundColor: '#e3f2fd',
                                borderWidth: 2
                            }
                        }}
                        disabled={!canUpload()}
                    >
                        העלה חשבונית חדשה
                    </Button>
                </Box>
            </Box>

            {openSearch && (
                <Box sx={styles.searchBarContainerStyled}>
                    <DynamicSearchBar
                        onClose={() => setOpenSearch(false)}
                        fields={fieldsToSearch}
                        onSearch={handleSearch}
                    />
                </Box>
            )}

            <Box sx={styles.mobileContent}>
                {hasNoSearchResults ? (
                    <StyledNoResultsOverlay>
                        <Typography variant="body1">
                            אין תוצאות לחיפוש זה
                        </Typography>
                    </StyledNoResultsOverlay>
                ) : paginatedData.length === 0 ? (
                    <Box sx={styles.noInvoices}>
                        <Typography variant="body1" color="text.secondary">אין חשבוניות להצגה</Typography>
                    </Box>
                ) : (
                    paginatedData.map(invoice => (
                        <InvoiceMobileCard
                            key={invoice.id}
                            invoice={invoice}
                            highlightedInvoiceId={hookData.highlightedInvoiceId}
                            highlightedInvoiceRef={highlightedInvoiceRef}
                            isOverdueAttention={isOverdueAttention}
                            onActionsClick={onActionsClick}
                            onEditClick={handleEditClick}
                            styles={styles}
                        />
                    ))
                )}
            </Box>

            <Box sx={styles.paginationContainer}>
                <TablePagination
                    component="div"
                    count={sortedData.length}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                    labelRowsPerPage="שורות בעמוד"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} מתוך ${count}`}
                />
            </Box>

            <Menu
                anchorEl={actionMenuAnchorEl}
                open={Boolean(actionMenuAnchorEl)}
                onClose={onActionsClose}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{
                    sx: styles.actionsMenu
                }}
            >
                {activeInvoiceId && paginatedData.find(inv => inv.id === activeInvoiceId) && (() => {
                    const invoice = paginatedData.find(inv => inv.id === activeInvoiceId);
                    const isPendingGoods = invoice.status === "ממתין לקבלת סחורה" || invoice.status === "חשבונית להמשך טיפול";
                    const isApproved = isApprovedStatus(invoice.status);
                    const requiresAttention = isRequiresAttentionStatus(invoice.status);
                    const isRequiresAttentionForRetry =
                        requiresAttention &&
                        invoice.approvalPackage?.status === APPROVAL_PACKAGE_STATUS.APPROVED_PRE_PRIORITY &&
                        invoice.approvalPackage?.priorityCreationFailed === true &&
                        invoice.approvalPackage?.allowRetry === true;

                    return (
                        <>
                            <MenuItem onClick={(e) => {
                                e.stopPropagation();
                                onRowClick(invoice);
                                onActionsClose();
                            }}>
                                <ReceiptLongIcon sx={styles.menuItemIcon} color="primary" />
                                <Typography sx={styles.menuItemText}>צפה בפרטי חשבונית</Typography>
                            </MenuItem>

                            <MenuItem onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(invoice);
                                onActionsClose();
                            }} disabled={!canEdit()}>
                                <Pencil size={18} color={canEdit() ? "#6B7280" : "#ccc"} />
                                <Typography sx={styles.menuItemText}>ערוך חשבונית</Typography>
                            </MenuItem>

                            {requiresAttention && (
                                <MenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditClick(invoice);
                                    onActionsClose();
                                }}>
                                    <AutorenewIcon sx={{ ...styles.menuItemIcon, color: '#E65100' }} />
                                    <Typography sx={{ ...styles.menuItemText, color: '#E65100' }}>עבד מחדש חשבונית</Typography>
                                </MenuItem>
                            )}

                            {isRequiresAttentionForRetry && (
                                <MenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    handleRetryRequiresAttentionInvoice(invoice.id);
                                    onActionsClose();
                                }}>
                                    <SendIcon sx={styles.menuItemIcon} color="primary" />
                                    <Typography sx={styles.menuItemText}>שליחה חוזרת לפריוריטי</Typography>
                                </MenuItem>
                            )}

                            {isApproved && (
                                <MenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    handleRetryApprovedInvoice(invoice.id);
                                    onActionsClose();
                                }}>
                                    <SendIcon sx={styles.menuItemIcon} color="primary" />
                                    <Typography sx={styles.menuItemText}>שליחה חוזרת לפריוריטי</Typography>
                                </MenuItem>
                            )}

                            {isPendingGoods && (
                                <MenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    handleCheckGoodsReceipt(invoice.id);
                                    onActionsClose();
                                }} disabled={checkingBooknum === invoice.id}>
                                    {checkingBooknum === invoice.id ? (
                                        <CircularProgress size={18} color="primary" />
                                    ) : (
                                        <RefreshIcon sx={styles.menuItemIcon} color="primary" />
                                    )}
                                    <Typography sx={styles.menuItemText}>בדוק קבלת סחורה</Typography>
                                </MenuItem>
                            )}

                            {isAdmin && (invoice.status === 'אושר ונקלט' || invoice.status === 'draft_in_priority') && (
                                <MenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeInvoiceStatus(invoice.id, 'מאושר');
                                    onActionsClose();
                                }}>
                                    <ChangeCircleIcon sx={{ ...styles.menuItemIcon, color: 'grey.700' }} />
                                    <Typography sx={styles.menuItemText}>שנה סטטוס למאושר</Typography>
                                </MenuItem>
                            )}

                            <Divider />

                            <Tooltip
                                title={
                                    !canDeleteInvoice(invoice)
                                        ? "אין אפשרות למחוק חשבונית בסטטוס אושר ונקלט "
                                        : !canDelete()
                                            ? "אין לך הרשאה למחוק חשבונית זו"
                                            : "מחק חשבונית"
                                }
                                arrow
                                placement="left"
                            >
                                <span>
                                    <MenuItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedFileName(invoice.fileName);
                                            setOpenDelete(true);
                                            onActionsClose();
                                        }}
                                        disabled={!canDelete() || !canDeleteInvoice(invoice)}
                                        sx={styles.deleteMenuItem}
                                    >
                                        <DeleteIcon sx={styles.menuItemIcon} />
                                        <Typography sx={styles.menuItemText}>מחק חשבונית</Typography>
                                    </MenuItem>
                                </span>
                            </Tooltip>
                        </>
                    );
                })()}
            </Menu>
        </>
    );
}

export default InvoiceMobileView;